import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw, AlertTriangle, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import paymentsService, { detectProvider, type PaymentStatus, type PaymentProvider } from '@/services/payments.service';
import { formatTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  paid: { label: 'Bezahlt', cls: 'bg-emerald-50 text-emerald-700' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-50 text-amber-700' },
  failed: { label: 'Fehlgeschlagen', cls: 'bg-rose-50 text-rose-700' },
  refunded: { label: 'Erstattet', cls: 'bg-slate-100 text-slate-600' },
  partially_refunded: { label: 'Teilw. erstattet', cls: 'bg-violet-50 text-violet-700' },
  unknown: { label: 'Unbekannt', cls: 'bg-slate-100 text-slate-500' },
};
const PROVIDER_LABEL: Record<string, string> = { paypal: 'PayPal', stripe: 'Stripe', klarna: 'Klarna', shopify: 'Shopify' };
const PROVIDER_COLOR: Record<string, string> = { paypal: '#003087', stripe: '#635BFF', klarna: '#D6699A', shopify: '#5E8E3E' };

const fmtTime = (iso: string) => {
  try { return formatTime(iso); } catch { return ''; }
};

const PaymentOrderStatus: React.FC<{ transactionId: string; paymentMethod?: string; total?: string; currency?: string; orderNumber?: string; provider?: PaymentProvider; organizationId?: string }> = ({ transactionId, paymentMethod, total, orderNumber, provider: explicitProvider, organizationId }) => {
  const { t } = useTranslation();
  const provider = explicitProvider ?? detectProvider(paymentMethod);
  const [st, setSt] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [refundStep, setRefundStep] = useState<0 | 1 | 2>(0); // 0 kapalı, 1 danger, 2 son onay
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!provider) return;
    setLoading(true);
    paymentsService.getStatus(provider, transactionId, organizationId).then(setSt).catch(() => setSt({ found: false, error: 'Fehler' })).finally(() => setLoading(false));
  };
  useEffect(() => { if (provider && transactionId) load(); }, [provider, transactionId]);

  if (!provider) return null;

  const scheduleRefund = async () => {
    setBusy(true);
    try {
      const r = await paymentsService.refund(provider, transactionId, { orderNumber, organizationId });
      if (r.success) { toast.success(t('common:x2.refundPlannedIn1h', 'Rückerstattung geplant — wird in ~1 Std. ausgeführt (stornierbar)')); load(); }
      else toast.error(`Fehlgeschlagen: ${r.message || ''}`);
    } catch { toast.error(t('agent:caseStatus.failed', 'Fehlgeschlagen')); }
    finally { setBusy(false); setRefundStep(0); setUnderstood(false); }
  };

  const cancelScheduled = async () => {
    if (!st?.scheduledRefund) return;
    setBusy(true);
    try {
      const r = await paymentsService.cancelRefund(st.scheduledRefund.id);
      if (r.success) { toast.success(t('common:x2.refundCancelled', 'Rückerstattung storniert')); load(); }
      else toast.error(r.message || 'Nicht stornierbar');
    } catch { toast.error(t('agent:caseStatus.failed', 'Fehlgeschlagen')); }
    finally { setBusy(false); }
  };

  const meta = st?.status ? STATUS_META[st.status] : null;
  const canRefund = st?.status === 'paid' || st?.status === 'partially_refunded';
  const amt = st?.amount ? `${st.amount.value} ${st.amount.currency_code}` : total;
  const scheduled = st?.scheduledRefund;

  return (
    <div className="ticket-order-payment-status">
      <span className="text-[11px] font-semibold" style={{ color: PROVIDER_COLOR[provider] }}>{PROVIDER_LABEL[provider]}:</span>
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
      ) : st?.found && meta ? (
        <>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>
          {amt && <span className="text-[11px] text-slate-500">{amt}</span>}

          {scheduled ? (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              <Clock className="h-3 w-3" /> {t('common:x1.refundPlanned', 'Erstattung geplant ·')} {fmtTime(scheduled.execute_at)}
              <button onClick={cancelScheduled} disabled={busy} className="ml-1 inline-flex items-center gap-0.5 text-rose-600 hover:underline">
                <X className="h-3 w-3" /> {t('common:action.cancel', 'Abbrechen')}
              </button>
            </span>
          ) : canRefund ? (
            <button onClick={() => { setRefundStep(1); setUnderstood(false); }} className="ticket-order-refund-action">
              <RotateCcw className="h-3 w-3" /> {t('settings:p2.refund', 'Rückerstattung')}
            </button>
          ) : null}
        </>
      ) : (
        <span className="text-[11px] text-slate-400">{st?.error || 'nicht gefunden'}</span>
      )}

      {/* İki adımlı danger onay */}
      <AlertDialog open={refundStep > 0} onOpenChange={(o) => { if (!o) { setRefundStep(0); setUnderstood(false); } }}>
        <AlertDialogContent>
          {refundStep === 1 ? (
            <>
              <AlertDialogHeader>
                <div className="mb-1 flex items-center gap-2 text-rose-600">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100"><AlertTriangle className="h-5 w-5" /></span>
                  <AlertDialogTitle className="text-rose-700">{t('common:x2.realRefund', 'Echte Rückerstattung')}</AlertDialogTitle>
                </div>
                <AlertDialogDescription className="text-slate-600">
                  <strong className="text-rose-600">{t('common:x2.attention', 'Achtung:')}</strong> {t('common:x2.itWill', 'Es wird')} <strong>echtes Geld</strong> {amt ? <>(<strong>{amt}</strong>) </> : ''}über {PROVIDER_LABEL[provider]} an den Kunden zurückgezahlt{orderNumber ? <> — Bestellung <strong>{orderNumber}</strong></> : ''}. Dieser Vorgang ist <strong>nicht umkehrbar</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); setRefundStep(2); }} className="bg-rose-600 hover:bg-rose-700">{t('common:action.next', 'Weiter')}</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('common:x2.confirmFinally', 'Endgültig bestätigen')}</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600">
                  {t('common:x2.refundWillBe', 'Die Rückerstattung wird')} <strong>erst in ca. 1 Stunde automatisch ausgeführt</strong> und kann bis dahin storniert werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="my-2 flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-0.5 h-4 w-4 accent-rose-600" />
                <span>{t('common:x2.iUnderstandThat', 'Ich verstehe, dass')} <strong>echtes Geld</strong> {amt ? <>({amt}) </> : ''}an den Kunden zurückgezahlt wird.</span>
              </label>
              <AlertDialogFooter>
                <button onClick={() => setRefundStep(1)} className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{t('common:action.back', 'Zurück')}</button>
                <AlertDialogAction onClick={(e) => { e.preventDefault(); scheduleRefund(); }} disabled={!understood || busy} className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rückerstattung planen'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PaymentOrderStatus;
